import { Accordion, Grid, Text } from "basis";
import { Separator } from "components/Separator/Separator";
import { capitaliseEveryWord, mapValueToLabel } from "library/utils";
import {
  applyingOnBehalfOfOtherOption,
  employmentStatusOptions,
} from "pages/HardshipApplication/options";
import { useHardshipApplicationContext } from "../../hooks/useHardshipApplicationContext";
import {
  getApplicantDetails,
  getBirthdate,
} from "../../reducer/hardshipSelectors";
import { TextCell } from "../TextCell";
import { AuthorisedPersonDetails } from "./AuthorisedPersonDetails";
import { getAddressString } from "./getAddressString";

type PersonalDetailsAccordionProps = {
  region: string;
};

export const PersonalDetailsAccordion = ({
  region,
}: PersonalDetailsAccordionProps) => {
  const { state } = useHardshipApplicationContext();
  const { prescreening, personalDetails } = state;

  const {
    firstName,
    lastName,
    email,
    contactNumber,
    address,
    employmentStatus,
  } = getApplicantDetails(personalDetails);

  return (
    <Accordion>
      <Accordion.Item initiallyOpen>
        <Accordion.Item.Header>Your personal details</Accordion.Item.Header>
        <Accordion.Item.Content>
          <Grid cols="8" colsGap-sm="4" rowsGap="8">
            <Grid.Item colSpan="all" colSpan-sm="0-3" rowSpan-sm="0">
              <Text as="h4" textStyle="heading6">
                Authorised person details
              </Text>
            </Grid.Item>

            <Grid.Item colSpan="all" colSpan-sm="0-3" rowSpan-sm="1">
              <Text as="h4" textStyle="heading6">
                <TextCell
                  header="Are you applying on behalf of other people?"
                  testId="on-behalf"
                >
                  {capitaliseEveryWord(
                    prescreening?.applyingOnBehalfOfOther &&
                      mapValueToLabel(
                        prescreening.applyingOnBehalfOfOther,
                        applyingOnBehalfOfOtherOption,
                      ),
                  )}
                </TextCell>
              </Text>
            </Grid.Item>
          </Grid>
          {prescreening?.applyingOnBehalfOfOther === "yes_on_behalf" && (
            <AuthorisedPersonDetails />
          )}
          <Grid cols="8" colsGap-sm="4" rowsGap="8" margin="4 0 0 0">
            <Grid.Item colSpan="all" colSpan-sm="0-8" rowSpan-sm="0">
              <Separator />
              <Text as="h4" textStyle="heading6" margin="4 0 0 0">
                Applicant details
              </Text>
            </Grid.Item>

            <Grid.Item colSpan="all" colSpan-sm="0-3" rowSpan-sm="1">
              <TextCell header="First name" testId="first-name">
                {capitaliseEveryWord(firstName)}
              </TextCell>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="4-8" rowSpan-sm="1">
              <TextCell header="Last name" testId="last-name">
                {capitaliseEveryWord(lastName)}
              </TextCell>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="0-3" rowSpan-sm="2">
              <TextCell header="Date of birth" testId="dob">
                {getBirthdate(personalDetails)}
              </TextCell>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="0-3" rowSpan-sm="3">
              <TextCell header="Email address" testId="email">
                {email}
              </TextCell>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="4-8" rowSpan-sm="3">
              <TextCell header="Contact number" testId="contact-number">
                {contactNumber}
              </TextCell>
            </Grid.Item>

            <Grid.Item colSpan="all" colSpan-sm="0-3" rowSpan-sm="4">
              <TextCell header="Address" testId="address">
                {address && getAddressString(address, region)}
              </TextCell>
            </Grid.Item>

            <Grid.Item colSpan="all" colSpan-sm="0-3" rowSpan-sm="5">
              <TextCell header="Employment status" testId="employment-status">
                {capitaliseEveryWord(
                  employmentStatus &&
                    mapValueToLabel(employmentStatus, employmentStatusOptions),
                )}
              </TextCell>
            </Grid.Item>
          </Grid>
        </Accordion.Item.Content>
      </Accordion.Item>
    </Accordion>
  );
};
